import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PackageCheck, ClipboardList, Timer, Truck, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import {
  DataTable,
  KpiCard,
  Panel,
  inr,
  num,
  type DataColumn,
} from "@/components/admin/analytics/AnalyticsKit";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { listPurchaseOrders, receivePurchaseOrder, deleteGoodsReceipt } from "@/lib/admin/inventory.functions";
import { PO_STATUS_LABEL, type PurchaseOrder } from "@/lib/admin/inventory.schemas";

export const Route = createFileRoute("/_admin/admin/goods-receipt")({
  head: () => ({
    meta: [
      { title: "Goods Receipt — Admin" },
      {
        name: "description",
        content:
          "Receive purchase orders into the kitchen store, book stock and generate the GRN reference.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: GoodsReceiptPage,
});

function today(): string {
  const now = new Date();
  return new Date(now.getTime() + (330 + now.getTimezoneOffset()) * 60_000)
    .toISOString()
    .slice(0, 10);
}

function GoodsReceiptPage() {
  const queryClient = useQueryClient();
  const listFn = useServerFn(listPurchaseOrders);
  const receiveFn = useServerFn(receivePurchaseOrder);
  const deleteGRNFn = useServerFn(deleteGoodsReceipt);

  const orders = useQuery({
    queryKey: ["admin", "inventory", "purchase-orders"],
    queryFn: () => listFn(),
    staleTime: 15_000,
  });

  const [receiving, setReceiving] = useState<PurchaseOrder | null>(null);
  const [receivedAt, setReceivedAt] = useState(today());
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<PurchaseOrder | null>(null);

  const pending = useMemo(
    () =>
      (orders.data ?? []).filter(
        (o) => o.status === "sent" || o.status === "partially_received" || o.status === "draft",
      ),
    [orders.data],
  );
  const receivedOrders = useMemo(
    () => (orders.data ?? []).filter((o) => o.status === "received"),
    [orders.data],
  );

  const receive = useMutation({
    mutationFn: (payload: unknown) => receiveFn({ data: payload as never }),
    onSuccess: async (res) => {
      toast.success(`Goods received — ${res.grn_number}`);
      setReceiving(null);
      await queryClient.invalidateQueries({ queryKey: ["admin", "inventory"] });
    },
    onError: () => toast.error("Couldn't book this receipt."),
  });

  const removeGRN = useMutation({
    mutationFn: (id: string) => deleteGRNFn({ data: { id } }),
    onSuccess: async () => {
      toast.success("Receipt reversed successfully");
      setConfirmDelete(null);
      await queryClient.invalidateQueries({ queryKey: ["admin", "inventory"] });
    },
    onError: () => toast.error("Couldn't reverse this receipt."),
  });

  // Booking a receipt permanently moves stock, so validate first and then
  // ask for an explicit confirmation like every other irreversible action.
  const requestBooking = () => {
    if (!receiving) return;
    const values = receiving.lines.map((l) => quantities[l.id] ?? 0);
    if (values.some((q) => !Number.isFinite(q) || q < 0)) {
      toast.error("Received quantities must be zero or more.");
      return;
    }
    if (values.every((q) => q === 0)) {
      toast.error("Enter a received quantity for at least one line.");
      return;
    }
    setConfirmOpen(true);
  };

  const bookReceipt = () => {
    if (!receiving) return;
    setConfirmOpen(false);
    receive.mutate({
      id: receiving.id,
      received_at: receivedAt,
      lines: receiving.lines.map((l) => ({
        line_id: l.id,
        received_quantity: quantities[l.id] ?? 0,
      })),
    });
  };

  const openReceipt = (po: PurchaseOrder) => {
    setReceiving(po);
    setReceivedAt(today());
    setQuantities(
      Object.fromEntries(po.lines.map((l) => [l.id, l.received_quantity || l.quantity])),
    );
  };

  const columns = (withAction: boolean): Array<DataColumn<PurchaseOrder>> => [
    {
      key: "po",
      header: "PO number",
      render: (r) => <span className="font-mono text-xs">{r.po_number}</span>,
      sortValue: (r) => r.po_number,
      exportValue: (r) => r.po_number,
    },
    {
      key: "supplier",
      header: "Supplier",
      render: (r) => <span className="font-medium text-foreground">{r.supplier_name}</span>,
      sortValue: (r) => r.supplier_name,
      exportValue: (r) => r.supplier_name,
    },
    {
      key: "expected",
      header: "Expected",
      render: (r) => r.expected_delivery ?? "—",
      sortValue: (r) => r.expected_delivery ?? "",
      exportValue: (r) => r.expected_delivery ?? "",
    },
    {
      key: "items",
      header: "Lines",
      align: "right",
      render: (r) => num(r.lines.length),
      sortValue: (r) => r.lines.length,
      exportValue: (r) => r.lines.length,
    },
    {
      key: "total",
      header: "Total",
      align: "right",
      render: (r) => inr(r.total_paise / 100),
      sortValue: (r) => r.total_paise,
      exportValue: (r) => r.total_paise / 100,
    },
    {
      key: "grn",
      header: "GRN",
      render: (r) => <span className="font-mono text-[11px]">{r.grn_number || "—"}</span>,
      exportValue: (r) => r.grn_number,
    },
    {
      key: "status",
      header: "Status",
      render: (r) => <Badge variant="secondary">{PO_STATUS_LABEL[r.status] ?? r.status}</Badge>,
      exportValue: (r) => PO_STATUS_LABEL[r.status] ?? r.status,
    },
    ...(withAction
      ? [
          {
            key: "actions",
            header: "Actions",
            align: "right" as const,
            render: (r: PurchaseOrder) => (
              <Button size="sm" className="h-8 gap-1" onClick={() => openReceipt(r)}>
                <PackageCheck className="h-3.5 w-3.5" aria-hidden />
                Receive
              </Button>
            ),
          },
        ]
      : [
          {
            key: "actions",
            header: "Actions",
            align: "right" as const,
            render: (r: PurchaseOrder) => (
              <div className="flex justify-end gap-1">
                <Button size="sm" variant="outline" className="h-8" onClick={() => setConfirmDelete(r)}>
                  <Trash2 className="h-3.5 w-3.5 text-destructive" aria-hidden />
                </Button>
              </div>
            ),
          },
        ]),
  ];

  return (
    <div>
      <AdminPageHeader
        title="Goods Receipt"
        description="Book delivered quantities into stock — every receipt updates inventory and writes the audit trail."
      />

      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiCard
          label="Awaiting receipt"
          value={num(pending.length)}
          tone="warn"
          icon={Timer}
          loading={orders.isPending}
        />
        <KpiCard
          label="Received orders"
          value={num(receivedOrders.length)}
          tone="success"
          icon={PackageCheck}
          loading={orders.isPending}
        />
        <KpiCard
          label="Open value"
          value={inr(pending.reduce((s, o) => s + o.total_paise, 0) / 100)}
          tone="info"
          icon={ClipboardList}
          loading={orders.isPending}
        />
        <KpiCard
          label="Suppliers pending"
          value={num(new Set(pending.map((o) => o.supplier_name)).size)}
          tone="teal"
          icon={Truck}
          loading={orders.isPending}
        />
      </div>

      <Panel title="Pending receipts" subtitle={`${pending.length} orders`} className="mb-4">
        <DataTable
          rows={pending}
          columns={columns(true)}
          loading={orders.isPending}
          exportName="pending-receipts"
          pageSize={10}
          minWidth={980}
          searchOn={(r) => `${r.po_number} ${r.supplier_name}`}
        />
      </Panel>

      <Panel title="Completed receipts" subtitle="Goods received notes">
        <DataTable
          rows={receivedOrders}
          columns={columns(false)}
          loading={orders.isPending}
          exportName="goods-receipts"
          importModule="goods-receipt"
          pageSize={10}
          minWidth={900}
          searchOn={(r) => `${r.po_number} ${r.supplier_name} ${r.grn_number}`}
        />
      </Panel>

      <Dialog open={receiving !== null} onOpenChange={(o) => (o ? null : setReceiving(null))}>
        <DialogContent className="max-h-[88vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Receive {receiving?.po_number}</DialogTitle>
          </DialogHeader>
          {receiving ? (
            <div className="space-y-3">
              <div className="w-48">
                <Label className="text-xs">Received on</Label>
                <Input
                  type="date"
                  className="mt-1 h-9"
                  value={receivedAt}
                  onChange={(e) => setReceivedAt(e.target.value)}
                />
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-[11px] uppercase text-muted-foreground">
                    <th className="py-2">Item</th>
                    <th className="py-2 text-right">Ordered</th>
                    <th className="py-2 text-right">Receiving</th>
                  </tr>
                </thead>
                <tbody>
                  {receiving.lines.map((l) => (
                    <tr key={l.id} className="border-b border-border/60">
                      <td className="py-2">{l.item_name}</td>
                      <td className="py-2 text-right tabular-nums">
                        {l.quantity} {l.unit}
                      </td>
                      <td className="py-2 text-right">
                        <Input
                          type="number"
                          step={0.01}
                          className="ml-auto h-9 w-32 text-right"
                          value={quantities[l.id] ?? 0}
                          onChange={(e) =>
                            setQuantities({ ...quantities, [l.id]: Number(e.target.value) })
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReceiving(null)}>
              Cancel
            </Button>
            <Button disabled={receive.isPending} onClick={requestBooking}>
              {receive.isPending ? "Booking…" : "Book receipt & update stock"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Book this goods receipt?</AlertDialogTitle>
            <AlertDialogDescription>
              Stock balances for {receiving?.po_number} will be updated and a GRN
              reference generated. Receipts cannot be reversed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={bookReceipt}>Book receipt</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmDelete !== null} onOpenChange={(o) => (o ? null : setConfirmDelete(null))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reverse receipt for {confirmDelete?.po_number}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will revert the status to 'Sent', clear the GRN, and deduct the received quantities from stock.
              The purchase order itself will NOT be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => confirmDelete && removeGRN.mutate(confirmDelete.id)}
            >
              Reverse receipt
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
